import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
export default Node.create({
  name: 'headline',
  key: 'headline',
  group: 'block',
  defining: true,
  content: 'inline*',

  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: 'body leading-[1.2] headline break-all',
        parseHTML: element => element.getAttribute('class') || '',
        renderHTML: attributes => {
          return {
            class: `${attributes.class}`,
          };
        },
      },
      textAlign: {
        default: 'inherit',
        parseHTML: element => element.style.textAlign || 'inherit',
        renderHTML: attributes => {
          const textAlign =
            attributes.textAlign == 'inherit'
              ? (this.options?.HTMLAttributes?.textAlign ?? 'inherit')
              : attributes.textAlign;
          if (!textAlign) {
            return {};
          }
          return {
            style: `text-align: ${textAlign}`,
          };
        },
      },
      maxLength: {
        default: this.options.HTMLAttributes?.maxLength ?? null,
        parseHTML: element => {
          const attr = element.getAttribute('max-length');
          return attr !== null ? parseInt(attr) : null;
        },
        renderHTML: attributes => {
          const maxLength =
            this.options.HTMLAttributes?.maxLength || attributes.maxLength;
          return {
            'max-length': maxLength,
          };
        },
      },
      sizeBreakpoint: {
        default: this.options.HTMLAttributes?.sizeBreakpoint ?? 50,
        parseHTML: element =>
          parseInt(element.getAttribute('size-breakpoint') || '50'),
        renderHTML: attributes => {
          const sizeBreakpoint =
            this.options.HTMLAttributes?.sizeBreakpoint ??
            attributes.sizeBreakpoint;
          return {
            'size-breakpoint': sizeBreakpoint,
          };
        },
      },
      fontSize: {
        default: this.options.HTMLAttributes?.fontSize ?? '18px',
        parseHTML: element =>
          element.getAttribute('font-size') || element.style.fontSize || '18px',
        renderHTML: attributes => {
          const fontSize =
            this.options.HTMLAttributes?.fontSize || attributes.fontSize;
          if (!fontSize) {
            return {};
          }
          return {
            style: `font-size: ${fontSize}`,
            'font-size': fontSize,
          };
        },
      },
      lineHeight: {
        default: this.options.HTMLAttributes?.lineHeight ?? '1.2',
        parseHTML: element =>
          element.getAttribute('line-height') ||
          element.style.lineHeight ||
          '1.2',
        renderHTML: attributes => {
          const lineHeight =
            this.options.HTMLAttributes?.lineHeight || attributes.lineHeight;
          if (!lineHeight) {
            return {};
          }
          return {
            style: `line-height: ${lineHeight}`,
            'line-height': lineHeight,
          };
        },
      },
      allowEnter: {
        default: true,
        parseHTML: element => element.hasAttribute('data-allow-enter'),
        renderHTML: attributes => {
          return attributes.allowEnter ? { 'data-allow-enter': 'true' } : {};
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'label',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { state, commands } = this.editor;
        const { $from } = state.selection;
        const node = $from.node();

        if (node.type.name === 'headline') {
          const allowEnter = node.attrs.allowEnter;
          if (allowEnter) {
            return commands.insertContent({ type: 'hardBreak' });
          }
          return true;
        }

        return false;
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        filterTransaction(transaction, state) {
          // Allow if nothing changed
          if (!transaction.docChanged) return true;

          const { doc, selection } = state;
          const { from, to } = selection;

          let block = false;

          doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name !== 'headline') return true;

            const { maxLength = null } = node.attrs;
            const currentText = node.textBetween(0, node.content.size, '', '');
            const currentLength = currentText.length;

            // Get selected text length (i.e. what will be replaced)
            const replacedLength = doc.textBetween(from, to, '', '').length;

            for (const step of transaction.steps) {
              //@ts-ignore
              if ('slice' in step && step.slice?.content?.size) {
                //@ts-ignore
                const insertedText = step.slice.content.textBetween(
                  0,
                  //@ts-ignore
                  step.slice.content.size,
                  '',
                  '',
                );

                const resultingLength =
                  currentLength - replacedLength + insertedText.length;

                if (maxLength && resultingLength > maxLength) {
                  block = true;
                  return false;
                }
              }
            }

            return true;
          });

          return !block;
        },

        appendTransaction(transactions, oldState, newState) {
          const tr = newState.tr;
          let modified = false;

          newState.doc.descendants((node, pos) => {
            if (node.type.name !== 'headline') return;

            const {
              maxLength = null,
              sizeBreakpoint = 50,
              fontSize: defaultFontSize,
              class: currentClass = '',
              lineHeight = 1.2,
            } = node.attrs;

            const text = node.textBetween(0, node.content.size, '', '');
            const isLong = text.length >= sizeBreakpoint;
            const atLimit = maxLength ? text.length >= maxLength : false;
            const fontSize = defaultFontSize ?? (isLong ? '12px' : '14px');
            let newClass = isLong ? 'body' : 'title_md';

            if (atLimit) {
              newClass += ' long-text';
            }
            let style = `line-height: ${lineHeight}; font-size: ${fontSize};`;
            if (newClass !== currentClass || fontSize !== node.attrs.fontSize) {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                class: newClass,
                style,
                fontSize,
                lineHeight,
              });
              modified = true;
            }
          });

          return modified ? tr : null;
        },
      }),
    ];
  },
});
